import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import {
  Answers,
  DescriptionItem,
  FormDefinition,
  Item,
  SectionItem,
} from './types';
import {
  allDescriptions,
  allFields,
  applyPrefill,
  buildWireAnswers,
  computeVisibility,
  initialAnswers,
  pickSuccessVariant,
  stepFields,
  validateFields,
} from './logic';
import { trackSubmission } from './analytics';
import { clearDetails, loadDetails, rememberedIds, saveDetails } from './deviceMemory';
import { batchCountFromAnswers, expandRepeats } from './repeat';
import { FieldView } from './FieldView';
import { Conversational } from './Conversational';

/** Collapsed header for an expander with no explicit summary. */
function summaryFromHtml(html: string): string {
  const m = /<strong>([^<]+)<\/strong>/.exec(html);
  return m ? m[1] : 'More information';
}

type LoadState =
  | { phase: 'loading' }
  | { phase: 'error' }
  | { phase: 'ready'; def: FormDefinition; version: number };

// Same-origin Shopify App Proxy mount: at /apps/order-form/* the API resolves
// under the proxied base (Shopify forwards to the Worker's /proxy/api/*);
// standalone (rpl-forms.com) keeps root-relative URLs.
const PROXY_PREFIX = '/apps/order-form';
const PROXY_MOUNTED = (() => {
  try {
    // Page path check covers the direct proxy URL (/apps/order-form); the
    // module-URL check covers theme-page mounts (/pages/... loading the bundle
    // from /apps/order-form/assets/app.js). Either means storefront + proxy.
    const p = window.location.pathname;
    if (p === PROXY_PREFIX || p.startsWith(`${PROXY_PREFIX}/`)) return true;
    return new URL(import.meta.url).pathname.startsWith(`${PROXY_PREFIX}/`);
  } catch {
    return false;
  }
})();
const API_BASE = PROXY_MOUNTED ? `${PROXY_PREFIX}/api` : '';

/**
 * Multi-tenant: the form slug comes from the URL path. rpl-forms.com/<slug>
 * serves that form; "/" and the legacy "/order" alias mean order-form. The
 * proxy mount is pinned to order-form (its prefix names the form).
 */
const SLUG = (() => {
  if (PROXY_MOUNTED) return 'order-form';
  try {
    const seg = window.location.pathname.split('/').filter(Boolean)[0] ?? '';
    if (seg === '' || seg === 'order' || seg === 'index.html') return 'order-form';
    return /^[a-z0-9][a-z0-9-]*$/i.test(seg) ? seg.toLowerCase() : 'order-form';
  } catch {
    return 'order-form';
  }
})();
const FORM_URL = `${API_BASE}/form/${SLUG}`;
const SUBMIT_URL = `${API_BASE}/submit/${SLUG}`;
/**
 * Legacy iframe embed: the PARENT page owns scrolling (postMessage protocol).
 * PROXY_MOUNTED takes precedence: a same-origin storefront mount scrolls its
 * own document, so it must NEVER take the iframe codepath (dialog anchoring,
 * scroll-lock skip, sticky chrome) even if the page happens to sit in a frame
 * (e.g. the Shopify theme editor preview).
 */
const IFRAMED = (() => {
  if (PROXY_MOUNTED) return false;
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
})();
/** Storefront provides the page chrome (iframe or proxy mount): no masthead. */
const EMBEDDED = IFRAMED || PROXY_MOUNTED;

/**
 * True when this document is OURS (standalone, iframe src, or the proxy shell
 * page) rather than a theme page that loaded our bundle into a div. Only then
 * may we paint the page background and margins on <body>.
 */
const OWN_DOCUMENT = (() => {
  if (!PROXY_MOUNTED) return true;
  try {
    const p = window.location.pathname;
    return p === PROXY_PREFIX || p.startsWith(`${PROXY_PREFIX}/`);
  } catch {
    return false;
  }
})();
if (OWN_DOCUMENT) document.body.classList.add('rplf-host');
/**
 * Legacy iframe embed: the PARENT sizes us from the height we report, so
 * nothing here may be viewport-tall. Inside a frame `svh` resolves to the
 * frame's own height, so a 100svh min-height feeds straight back into the
 * next height report and the iframe grows on every tick. Our height must be
 * purely intrinsic; this class switches those rules off.
 */
if (IFRAMED) document.body.classList.add('rplf-iframed');

/** Root class: the proxy modifier switches the bottom bar to viewport-fixed. */
const PAGE_CLASS = PROXY_MOUNTED ? 'page proxy-mounted' : 'page';

/**
 * Kiosk mode (conversational forms on the in-store iPad): after the success
 * screen sits idle this long, fade back to the welcome screen with all state
 * cleared so the next customer starts fresh.
 */
const KIOSK_RESET_SECONDS = 20;
const KIOSK_FADE_MS = 400;

/**
 * Kiosk forms whose success screen tells staff when the new customer can be
 * found in Square POS (owner 2026-10-01). Staff search Square the moment the
 * iPad is handed back, but Square's search trails a new profile by up to ~30 s,
 * so RewindBackend watches Square's search and the Worker reports it at
 * GET /status/:slug/:id. The kiosk reset waits until this line has settled.
 */
const CONFIRMS_SQUARE_READY = new Set(['new']);
const STATUS_POLL_MS = 1500;
const STATUS_GIVE_UP_MS = 100_000;
const STATUS_MAX_MISSES = 6;
type Readiness = 'off' | 'waiting' | 'ready' | 'slow';

function postToParent(message: Record<string, unknown>) {
  try {
    if (window.parent && window.parent !== window) {
      window.parent.postMessage(message, '*');
    }
  } catch {
    // Embedding parent may be cross-origin restricted; never let this throw.
  }
}

function scrollTopIntoView() {
  window.scrollTo({ top: 0, behavior: 'smooth' });
  postToParent({ type: 'rpl-form-scroll-top' });
}

export function App() {
  const [load, setLoad] = useState<LoadState>({ phase: 'loading' });
  const [stepIndex, setStepIndex] = useState(0);
  const [answers, setAnswers] = useState<Answers>({});
  const [otherText, setOtherText] = useState<Record<string, string>>({});
  const [confirmEmail, setConfirmEmail] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [banner, setBanner] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [rpl, setRpl] = useState<string | null>(null);
  const [hp, setHp] = useState('');
  const [openDialog, setOpenDialog] = useState<string | null>(null);
  const [resetFading, setResetFading] = useState(false);
  const [readiness, setReadiness] = useState<Readiness>('off');
  const [signupId, setSignupId] = useState<number | null>(null);
  const [prefilled, setPrefilled] = useState<Set<string>>(new Set());
  /** Field ids restored from this device's own last order (deviceMemory.ts). */
  const [restored, setRestored] = useState<Set<string>>(new Set());
  const prevVisibleDialogs = useRef<Set<string>>(new Set());
  const pageRef = useRef<HTMLDivElement>(null);

  /** "Not you?" on the confirm screen: forget this device, clear its values. */
  const forgetDevice = useCallback(() => {
    if (load.phase !== 'ready') return;
    clearDetails(load.def);
    const ids = rememberedIds(load.def);
    setAnswers((a) => {
      const next = { ...a };
      for (const id of ids) delete next[id];
      return next;
    });
    setRestored(new Set());
  }, [load]);

  // ---- load the definition ------------------------------------------------
  const fetchDefinition = useCallback(async () => {
    setLoad({ phase: 'loading' });
    try {
      const res = await fetch(FORM_URL, { headers: { accept: 'application/json' } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data: { version?: number; data?: FormDefinition; definition?: FormDefinition } & FormDefinition =
        await res.json();
      // Worker returns {version, data}; tolerate {version, definition} or a bare doc too.
      const def: FormDefinition = data.data ?? data.definition ?? data;
      const version = Number(data.version ?? 1) || 1;
      if (!def || !Array.isArray(def.steps) || def.steps.length === 0) throw new Error('bad definition');
      const initial = initialAnswers(def);
      const fromUrl = applyPrefill(def, initial, window.location.search);
      setPrefilled(fromUrl);
      // Device memory fills only what the URL didn't: an explicit link always
      // describes the person in front of us better than this browser's history.
      const saved = loadDetails(def);
      const restoredIds = new Set<string>();
      for (const [id, value] of Object.entries(saved)) {
        if (fromUrl.has(id)) continue;
        initial[id] = value;
        restoredIds.add(id);
      }
      setRestored(restoredIds);
      setAnswers(initial);
      setLoad({ phase: 'ready', def, version });
    } catch {
      setLoad({ phase: 'error' });
    }
  }, []);

  useEffect(() => {
    void fetchDefinition();
  }, [fetchDefinition]);

  // ---- app-shell height (storefront mount) --------------------------------
  // The theme header and page title sit above us, so our region is the
  // viewport MINUS wherever we start. Measured rather than guessed, then used
  // by the mobile layout to give the form a fixed height: the question body
  // scrolls inside it and the primary button never leaves the bottom of the
  // screen. Without this, a long terms screen scrolls the whole page and the
  // button walks off the bottom.
  useEffect(() => {
    if (!PROXY_MOUNTED) return;
    const el = pageRef.current;
    if (!el) return;
    // Viewport-relative, NOT document-relative: this is "how much screen is
    // left below us", which stays correct even if the page has scrolled.
    const measure = () => {
      const top = Math.max(0, Math.round(el.getBoundingClientRect().top));
      el.style.setProperty('--rplf-top', `${top}px`);
    };
    measure();
    // The theme header grows when its webfont lands and when its logo decodes,
    // which pushed us down AFTER a one-shot measurement and left the button
    // hanging below the fold. Re-measure on anything that can move us.
    const ro = new ResizeObserver(measure);
    ro.observe(document.body);
    document.fonts?.ready.then(measure).catch(() => {});
    window.addEventListener('resize', measure);
    window.addEventListener('orientationchange', measure);
    window.addEventListener('scroll', measure, { passive: true });
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
      window.removeEventListener('orientationchange', measure);
      window.removeEventListener('scroll', measure);
    };
  }, [load.phase]);

  // ---- iframe height reporting -------------------------------------------
  useEffect(() => {
    const post = () =>
      postToParent({ type: 'rpl-form-height', height: document.body.scrollHeight });
    post();
    const ro = new ResizeObserver(post);
    ro.observe(document.body);
    return () => ro.disconnect();
  }, []);

  // ---- derived state ------------------------------------------------------
  const rawDef = load.phase === 'ready' ? load.def : null;
  /** Batches the answers describe; adding one re-expands the definition. */
  const batches = useMemo(
    () => (rawDef ? batchCountFromAnswers(rawDef, answers) : 1),
    [rawDef, answers],
  );
  const def = useMemo(
    () => (rawDef ? expandRepeats(rawDef, batches) : null),
    [rawDef, batches],
  );
  const visibility = useMemo(
    () => (def ? computeVisibility(def, answers, otherText) : null),
    [def, answers, otherText],
  );

  // A newly added batch arrives with its own defaults (Online Upload, country
  // Australia); initialAnswers only ever saw the first copy.
  useEffect(() => {
    if (!def) return;
    setAnswers((a) => {
      let changed = false;
      const next: Answers = { ...a };
      for (const f of allFields(def)) {
        if (f.default !== undefined && next[f.id] === undefined) {
          next[f.id] = f.default as string | string[];
          changed = true;
        }
      }
      return changed ? next : a;
    });
  }, [def]);

  // ---- agree-dialog descriptions ------------------------------------------
  const dialogItems = useMemo(
    () => (def ? allDescriptions(def).filter((d) => d.presentation === 'dialog') : []),
    [def],
  );

  // Auto-open a dialog description the moment it becomes visible (e.g. the
  // terms sheet when the submit method changes). Agreeing closes it; it only
  // re-opens when it goes hidden and comes back (selection changed).
  useEffect(() => {
    // Conversational mode presents dialog descriptions as their own screens.
    if (!visibility || def?.flow === 'conversational') return;
    const nowVisible = new Set(
      dialogItems.filter((d) => visibility.visibleItems.has(d.id)).map((d) => d.id),
    );
    const prev = prevVisibleDialogs.current;
    prevVisibleDialogs.current = nowVisible;
    const newly = [...nowVisible].find((id) => !prev.has(id));
    if (newly) setOpenDialog(newly);
    else if (openDialog && !nowVisible.has(openDialog)) setOpenDialog(null);
  }, [visibility, dialogItems, openDialog]);

  // Lock background scrolling while a dialog sheet is open (standalone only;
  // embedded pages scroll in the parent, which we cannot and should not lock).
  useEffect(() => {
    if (!openDialog) return;
    postToParent({ type: 'rpl-form-scroll-top' });
    // Lock scrolling whenever THIS document scrolls (standalone + proxy mount);
    // in the legacy iframe the parent scrolls and we cannot/should not lock it.
    if (!IFRAMED) {
      const previous = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = previous;
      };
    }
  }, [openDialog]);

  const setValue = useCallback((id: string, value: string | string[]) => {
    setAnswers((a) => ({ ...a, [id]: value }));
    setErrors((e) => {
      if (!e[id] && !e[`${id}__confirm`]) return e;
      const next = { ...e };
      delete next[id];
      delete next[`${id}__confirm`];
      return next;
    });
  }, []);

  const setOther = useCallback((id: string, text: string) => {
    setOtherText((o) => ({ ...o, [id]: text }));
    setErrors((e) => {
      if (!e[id]) return e;
      const next = { ...e };
      delete next[id];
      return next;
    });
  }, []);

  const setConfirm = useCallback((id: string, text: string) => {
    setConfirmEmail((c) => ({ ...c, [id]: text }));
    setErrors((e) => {
      if (!e[`${id}__confirm`]) return e;
      const next = { ...e };
      delete next[`${id}__confirm`];
      return next;
    });
  }, []);

  // ---- kiosk auto-reset (conversational forms on the in-store iPad) -------
  const resetForNextCustomer = useCallback(() => {
    if (!def) return;
    const initial = initialAnswers(def);
    setPrefilled(applyPrefill(def, initial, window.location.search));
    setAnswers(initial);
    setOtherText({});
    setConfirmEmail({});
    setErrors({});
    setBanner(null);
    setHp('');
    setOpenDialog(null);
    prevVisibleDialogs.current = new Set();
    setRpl(null);
    setReadiness('off');
    setSignupId(null);
    setResetFading(false);
    scrollTopIntoView();
  }, [def]);

  useEffect(() => {
    // While the Square line still says "adding you", hold the reset: staff
    // may be looking at the iPad to know when to search.
    if (!rpl || def?.flow !== 'conversational' || readiness === 'waiting') return;
    const t = window.setTimeout(() => setResetFading(true), KIOSK_RESET_SECONDS * 1000);
    return () => window.clearTimeout(t);
  }, [rpl, def, readiness]);

  // ---- kiosk: is the new customer findable in Square yet? -----------------
  useEffect(() => {
    if (readiness !== 'waiting' || signupId === null) return;
    const started = Date.now();
    let misses = 0;
    let stopped = false;
    let timer = 0;
    const tick = async () => {
      if (stopped) return;
      if (Date.now() - started > STATUS_GIVE_UP_MS) {
        setReadiness('slow');
        return;
      }
      try {
        const res = await fetch(`${API_BASE}/status/${SLUG}/${signupId}`, {
          headers: { accept: 'application/json' },
          cache: 'no-store',
        });
        const data = res.ok ? ((await res.json()) as { state?: string }) : null;
        if (stopped) return;
        if (data?.state === 'ready') {
          setReadiness('ready');
          return;
        }
        misses = data?.state ? 0 : misses + 1;
      } catch {
        misses += 1;
      }
      // A Worker without this endpoint answers with the SPA shell, not JSON:
      // after a few of those, fall back to the plain success screen.
      if (misses >= STATUS_MAX_MISSES) {
        if (!stopped) setReadiness('off');
        return;
      }
      timer = window.setTimeout(tick, STATUS_POLL_MS);
    };
    timer = window.setTimeout(tick, STATUS_POLL_MS);
    return () => {
      stopped = true;
      window.clearTimeout(timer);
    };
  }, [readiness, signupId]);

  useEffect(() => {
    if (!resetFading) return;
    const t = window.setTimeout(resetForNextCustomer, KIOSK_FADE_MS);
    return () => window.clearTimeout(t);
  }, [resetFading, resetForNextCustomer]);

  const focusFirstError = useCallback(() => {
    window.setTimeout(() => {
      const el = document.querySelector('.field-error');
      el?.closest('.field')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 30);
  }, []);

  // ---- navigation ---------------------------------------------------------
  const validateStepAt = useCallback(
    (index: number): Record<string, string> => {
      if (!def || !visibility) return {};
      const fields = stepFields(def.steps[index]).filter((f) =>
        visibility.visibleItems.has(f.id),
      );
      return validateFields(fields, answers, otherText, confirmEmail);
    },
    [def, visibility, answers, otherText, confirmEmail],
  );

  const goNext = useCallback(() => {
    if (!def) return;
    const errs = validateStepAt(stepIndex);
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      focusFirstError();
      return;
    }
    setErrors({});
    setBanner(null);
    setStepIndex((i) => Math.min(i + 1, def.steps.length - 1));
    scrollTopIntoView();
  }, [def, stepIndex, validateStepAt, focusFirstError]);

  const goBack = useCallback(() => {
    setErrors({});
    setBanner(null);
    setStepIndex((i) => Math.max(i - 1, 0));
    scrollTopIntoView();
  }, []);

  // ---- submit -------------------------------------------------------------
  const submit = useCallback(async () => {
    if (!def || !visibility || load.phase !== 'ready' || submitting) return;

    // Validate every step's visible fields; jump back if an earlier one broke.
    for (let i = 0; i < def.steps.length; i++) {
      const errs = validateStepAt(i);
      if (Object.keys(errs).length > 0) {
        setErrors(errs);
        if (i !== stepIndex) setStepIndex(i);
        focusFirstError();
        return;
      }
    }

    setSubmitting(true);
    setBanner(null);
    try {
      const payload = {
        version: load.version,
        // FLAT wire-keyed answers, batch prefixes and gate answers included.
        // The SERVER groups them into `Batches` (routes/public.ts) — it has to
        // see the gates to know how many batches to validate, and grouping
        // here as well stripped exactly the keys it needed.
        answers: buildWireAnswers(def, visibility.visibleItems, answers, otherText),
        _hp: hp,
      };
      const res = await fetch(SUBMIT_URL, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (res.status === 422) {
        const data = await res.json().catch(() => ({}));
        applyServerErrors(data);
        return;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const data = await res.json();
      const raw = String(data.rpl ?? data.id ?? '');
      const ref = raw.toUpperCase().startsWith('RPL') ? raw.toUpperCase() : `RPL${raw}`;
      setRpl(ref);
      const newId = Number(data.id);
      if (CONFIRMS_SQUARE_READY.has(SLUG) && Number.isSafeInteger(newId) && newId > 0) {
        setSignupId(newId);
        setReadiness('waiting');
      }
      // Remember this device's own details for next time (identity + address
      // only; see deviceMemory.ts). Saved AFTER success so a failed attempt
      // never leaves stale details behind.
      saveDetails(def, answers);
      // Conversion tracking: one dataLayer event, GTM decides what fires.
      trackSubmission(def, ref, answers, batches);
      postToParent({ type: 'rpl-form-success', rpl: ref });
      scrollTopIntoView();
    } catch {
      setBanner(
        'We could not send your order just now. Please check your connection and try again. Your answers have been kept.',
      );
    } finally {
      setSubmitting(false);
    }

    function applyServerErrors(data: unknown) {
      const fieldErrs: Record<string, string> = {};
      const unmatched: string[] = [];
      const fields = def ? allFields(def) : [];
      const record =
        data && typeof data === 'object' && 'errors' in data
          ? (data as { errors: unknown }).errors
          : data;

      if (record && typeof record === 'object' && !Array.isArray(record)) {
        for (const [key, msg] of Object.entries(record as Record<string, unknown>)) {
          const f = fields.find((x) => x.id === key || x.wire === key);
          if (f) fieldErrs[f.id] = String(msg);
          else unmatched.push(`${key}: ${String(msg)}`);
        }
      } else if (Array.isArray(record)) {
        for (const entry of record) {
          const e = entry as { field?: string; message?: string };
          const f = fields.find((x) => x.id === e.field || x.wire === e.field);
          if (f) fieldErrs[f.id] = e.message ?? 'Please check this field.';
          else unmatched.push(e.message ?? String(entry));
        }
      }

      if (Object.keys(fieldErrs).length > 0) {
        setErrors(fieldErrs);
        // Jump to the first step containing a server-reported error.
        if (def) {
          const idx = def.steps.findIndex((s) =>
            stepFields(s).some((f) => fieldErrs[f.id]),
          );
          if (idx >= 0 && idx !== stepIndex) setStepIndex(idx);
        }
        focusFirstError();
      }
      setBanner(
        unmatched.length > 0
          ? `Please review your order: ${unmatched.join(' ')}`
          : Object.keys(fieldErrs).length > 0
            ? 'Please fix the highlighted fields and submit again.'
            : 'Your order could not be accepted. Please review your answers and try again.',
      );
    }
  }, [
    def,
    visibility,
    load,
    submitting,
    answers,
    otherText,
    hp,
    stepIndex,
    validateStepAt,
    focusFirstError,
  ]);

  // ---- render -------------------------------------------------------------
  if (load.phase === 'loading') {
    return (
      <div className={PAGE_CLASS} ref={pageRef}>
        <div className="loading">Loading the order form...</div>
      </div>
    );
  }

  if (load.phase === 'error' || !def || !visibility) {
    return (
      <div className={PAGE_CLASS} ref={pageRef}>
        <div className="card center-card">
          <p>The order form could not be loaded. Please check your connection.</p>
          <button className="btn btn-primary" onClick={() => void fetchDefinition()}>
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (rpl) {
    return (
      <div className={PAGE_CLASS} ref={pageRef}>
        {!EMBEDDED && def.flow !== 'conversational' && (
        <header className="masthead">
          <h1>{def.title}</h1>
          {def.subtitle && <p className="subtitle">{def.subtitle}</p>}
        </header>
        )}
        <div className={`success-shell${resetFading ? ' success-fading' : ''}`}>
          <SuccessScreen
            rpl={rpl}
            def={def}
            variant={pickSuccessVariant(def, answers, otherText)}
            readiness={readiness}
          />
          {def.flow === 'conversational' && (
            <div className="success-reset-row">
              <button
                type="button"
                className="link-btn"
                onClick={() => setResetFading(true)}
              >
                Start again
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (def.flow === 'conversational') {
    return (
      <div className={`${PAGE_CLASS} conv-page`} ref={pageRef}>
        {/* Honeypot: hidden from humans, tempting for bots. */}
        <input
          type="text"
          name="_hp"
          className="hp"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          value={hp}
          onChange={(e) => setHp(e.target.value)}
        />
        <Conversational
          def={def}
          visibleItems={visibility.visibleItems}
          prefilled={prefilled}
          restored={restored}
          onForget={forgetDevice}
          answers={answers}
          otherText={otherText}
          confirmEmail={confirmEmail}
          errors={errors}
          banner={banner}
          submitting={submitting}
          onValue={setValue}
          onOtherText={setOther}
          onConfirm={setConfirm}
          setErrors={setErrors}
          submit={() => void submit()}
        />
      </div>
    );
  }

  const step = def.steps[stepIndex];
  const isLast = stepIndex === def.steps.length - 1;

  const renderItems = (items: Item[]): ReactNode =>
    items.map((it) => {
      if (!visibility.visibleItems.has(it.id)) return null;
      if (it.kind === 'section') return renderSection(it);
      if (it.kind === 'description') return renderDescription(it);
      if (it.hidden) return null; // stays in the payload, never on screen
      return (
        <FieldView
          key={it.id}
          field={it}
          value={answers[it.id]}
          otherText={otherText[it.id] ?? ''}
          confirmValue={confirmEmail[it.id] ?? ''}
          error={errors[it.id]}
          confirmError={errors[`${it.id}__confirm`]}
          onValue={setValue}
          onOtherText={setOther}
          onConfirm={setConfirm}
        />
      );
    });

  function renderSection(s: SectionItem) {
    return (
      <section key={s.id} className="card section-card">
        {s.title && <h2 className="section-title">{s.title}</h2>}
        {s.description && <p className="section-desc">{s.description}</p>}
        {renderItems(s.items)}
      </section>
    );
  }

  function renderDescription(d: DescriptionItem) {
    if (d.presentation === 'dialog') {
      // The full text lives in the modal sheet; inline we show a compact
      // agreed row with a way to re-read it.
      return (
        <div key={d.id} className="terms-row">
          <span className="tick on" aria-hidden="true">
            <span
              style={{
                color: '#1A1A1A',
                fontSize: '13px',
                lineHeight: 1,
                fontFamily: 'Arial, sans-serif',
                fontWeight: 700,
                display: 'block',
              }}
            >
              {'✓'}
            </span>
          </span>
          <span className="terms-text">You have agreed to these service terms.</span>
          <button type="button" className="link-btn" onClick={() => setOpenDialog(d.id)}>
            View
          </button>
        </div>
      );
    }
    // 'info' collapses to the same disclosure row in the classic flow, and a
    // 'prompt' (a Continue-time gate in the conversational flow) degrades to
    // an always-visible block rather than vanishing.
    if (d.presentation === 'expander' || d.presentation === 'info') {
      return (
        <details key={d.id} className="expander">
          <summary>
            <span className="expander-icon" aria-hidden="true">
              i
            </span>
            <span className="expander-title">{d.summary ?? summaryFromHtml(d.html)}</span>
            <span
              className="expander-chev"
              aria-hidden="true"
              style={{
                color: '#888888',
                fontSize: '12px',
                lineHeight: 1,
                fontFamily: 'Arial, sans-serif',
                display: 'block',
              }}
            >
              {'▾'}
            </span>
          </summary>
          <div
            className="description expander-body"
            dangerouslySetInnerHTML={{ __html: d.html }}
          />
        </details>
      );
    }
    return (
      <div
        key={d.id}
        className="description"
        dangerouslySetInnerHTML={{ __html: d.html }}
      />
    );
  }

  const openDialogItem = openDialog
    ? dialogItems.find((d) => d.id === openDialog) ?? null
    : null;

  return (
    <div className={PAGE_CLASS} ref={pageRef}>
      {!EMBEDDED && (
      <header className="masthead">
        <h1>{def.title}</h1>
        {def.subtitle && <p className="subtitle">{def.subtitle}</p>}
      </header>
      )}

      {def.steps.length > 1 && (
        <div className="app-bar" role="group" aria-label="Form progress">
          <div className="app-bar-row">
            <span className="app-bar-title">
              {step.title || `Step ${stepIndex + 1} of ${def.steps.length}`}
            </span>
          </div>
          <div className="app-bar-track">
            {def.steps.map((s, i) => (
              <span
                key={s.id}
                className={
                  'app-bar-seg' + (i < stepIndex ? ' done' : '') + (i === stepIndex ? ' active' : '')
                }
              />
            ))}
          </div>
        </div>
      )}

      <form
        className="step"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          if (isLast) void submit();
          else goNext();
        }}
      >
        {/* Honeypot: hidden from humans, tempting for bots. */}
        <input
          type="text"
          name="_hp"
          className="hp"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          value={hp}
          onChange={(e) => setHp(e.target.value)}
        />

        <div className="card step-card">{renderItems(step.items)}</div>

        {banner && (
          <div className="banner" role="alert">
            {banner}
          </div>
        )}

        <div className="action-bar">
          {stepIndex > 0 && (
            <button type="button" className="btn btn-ghost" onClick={goBack}>
              Back
            </button>
          )}
          <button type="submit" className="btn btn-primary btn-grow" disabled={submitting}>
            {isLast ? (submitting ? 'Sending...' : def.submitLabel ?? 'Submit') : 'Next'}
          </button>
        </div>
      </form>

      {openDialogItem && (
        <div className={`dialog-overlay${IFRAMED ? ' embedded' : ''}`} role="presentation">
          <div className="dialog-sheet" role="dialog" aria-modal="true" aria-label="Service terms">
            <div
              className="description dialog-body"
              dangerouslySetInnerHTML={{ __html: openDialogItem.html }}
            />
            <div className="dialog-actions">
              <button
                type="button"
                className="btn btn-primary btn-block"
                onClick={() => setOpenDialog(null)}
              >
                {openDialogItem.agreeLabel ?? 'I Agree'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Success celebration: gold ring draw, tick draw, radial glow pulse and a
 * one-shot confetti burst. Transform/opacity only; the whole sequence lands
 * inside ~900ms. Particle vectors are deterministic (no layout thrash, no
 * re-randomising between renders).
 */
const CONFETTI = Array.from({ length: 28 }, (_, i) => {
  const angle = (i / 28) * Math.PI * 2 + (i % 3) * 0.22;
  const distance = 62 + ((i * 37) % 46);
  return {
    x: Math.round(Math.cos(angle) * distance),
    y: Math.round(Math.sin(angle) * distance),
    rotate: (i % 2 === 0 ? 1 : -1) * (120 + ((i * 53) % 160)),
    delay: (i * 17) % 60,
    tone: i % 3,
    round: i % 4 === 0,
  };
});

function Celebration() {
  return (
    <div className="success-anim" aria-hidden="true">
      <span className="anim-glow" />
      {/* SVG must stay for the stroke-draw animation, so every paint property
          is inline; only dash geometry + animation live in CSS (so the
          reduced-motion override can still zero the offsets). */}
      <svg viewBox="0 0 72 72" style={{ width: '72px', height: '72px', display: 'block' }}>
        <circle
          className="anim-ring"
          cx="36"
          cy="36"
          r="32"
          style={{ fill: 'none', stroke: '#EBCE00', strokeWidth: 4, strokeLinecap: 'round' }}
        />
        <path
          className="anim-tick"
          d="M22 37.5 32 47.5 51 27"
          style={{
            fill: 'none',
            stroke: '#1A1A1A',
            strokeWidth: 5,
            strokeLinecap: 'round',
            strokeLinejoin: 'round',
          }}
        />
      </svg>
      <div className="confetti-layer">
        {CONFETTI.map((p, i) => (
          <span
            key={i}
            className={`confetti tone-${p.tone}${p.round ? ' round' : ''}`}
            style={
              {
                '--cx': `${p.x}px`,
                '--cy': `${p.y}px`,
                '--cr': `${p.rotate}deg`,
                '--cd': `${p.delay}ms`,
              } as CSSProperties
            }
          />
        ))}
      </div>
    </div>
  );
}

/**
 * Success HTML that can carry <script> tags (e.g. the Elfsight reviews
 * widget). innerHTML never executes scripts, so each one is re-created as a
 * real script element after injection.
 *
 * TRUST BOUNDARY (read before building the phase-6 admin form editor):
 * executing definition-supplied script is acceptable TODAY because
 * definitions are staff-only (FORMS_LAB_TOKEN gate) - the same trust level
 * as the "sanitised static HTML" description blocks. The moment definition
 * editing gets a UI with broader access, this becomes a stored-XSS
 * escalation path: the editor MUST strip/deny <script> in saved HTML or
 * gate successVariants/successHtml behind a separate review step.
 */
function HtmlWithScripts({ html, className }: { html: string; className: string }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const host = ref.current;
    if (!host) return;
    host.innerHTML = html;
    for (const old of Array.from(host.querySelectorAll('script'))) {
      const script = document.createElement('script');
      for (const attr of Array.from(old.attributes)) {
        script.setAttribute(attr.name, attr.value);
      }
      script.text = old.text;
      old.replaceWith(script);
    }
    return () => {
      host.innerHTML = '';
    };
  }, [html]);
  return <div className={className} ref={ref} />;
}

/**
 * Staff-facing line under the signup kiosk's success copy: tells whoever is
 * about to search Square POS whether the new customer will come up yet.
 */
function SquareReadyLine({ readiness }: { readiness: Readiness }) {
  if (readiness === 'off') return null;
  if (readiness === 'ready') {
    return (
      <p className="square-ready is-ready" role="status" aria-live="polite">
        <span className="square-ready-tick" aria-hidden="true">✓</span>
        Our team can find you now
      </p>
    );
  }
  if (readiness === 'slow') {
    return (
      <p className="square-ready" role="status" aria-live="polite">
        Still adding you. Our team may need another minute to find you.
      </p>
    );
  }
  return (
    <p className="square-ready" role="status" aria-live="polite">
      <span className="square-ready-spinner" aria-hidden="true" />
      Adding you to our system…
    </p>
  );
}

function SuccessScreen({
  rpl,
  def,
  variant,
  readiness = 'off',
}: {
  rpl: string;
  def: FormDefinition;
  variant: { title?: string; html: string } | null;
  readiness?: Readiness;
}) {
  // Forms with their own success copy get a generic screen: title, reference
  // box, then the definition's HTML (conditional variant first, if one
  // matched). Otherwise the order-form default below.
  const successHtml = variant?.html ?? def.successHtml;
  const successTitle = variant?.title ?? def.successTitle;
  if (successHtml) {
    return (
      <div className="success">
        <div className="card success-card">
          {def.successAnimation === 'tick' && <Celebration />}
          <h2>{successTitle ?? 'Thank you!'}</h2>
          {def.showReference !== false && (
            <div className="rpl-box">
              <span className="rpl-caption">Your reference number</span>
              <span className="rpl-number">{rpl}</span>
            </div>
          )}
          <HtmlWithScripts className="success-body" html={successHtml} />
          <SquareReadyLine readiness={readiness} />
        </div>
      </div>
    );
  }

  return (
    <div className="success">
      <div className="card success-card">
        <h2>{def.successTitle ?? 'Thank you! Your order has been received.'}</h2>
        <div className="rpl-box">
          <span className="rpl-caption">Your reference number</span>
          <span className="rpl-number">{rpl}</span>
        </div>
        <p className="rpl-instruction">
          Write <strong>{rpl}</strong> on or in your package so we can match it to your
          order.
        </p>
      </div>

      <div className="card">
        <h3>What happens next</h3>
        <ul className="next-list">
          <li>Send in or drop off your film with your reference number on the package.</li>
          <li>We will email your invoice when your film arrives at the lab.</li>
          <li>Processing starts as soon as payment is received.</li>
        </ul>
      </div>

      <div className="addresses">
        <div className="card address-card">
          <h3>Mail In - Sydney store only</h3>
          <p>
            Rewind Photo Lab
            <br />
            338-356 Mitchell Road
            <br />
            Alexandria NSW 2015
          </p>
          <p className="address-note">
            Please note: mail-in orders can only be posted to our Sydney store. We
            cannot receive mailed film at Wollongong.
          </p>
        </div>
        <div className="card address-card">
          <h3>Dropbox - RPL Front Door, Alexandria</h3>
          <p className="address-note">
            Bags to write on are provided at the front door. Write your name and reference
            number on a bag, seal your film inside, and post it through the slot in
            the front door. Available after hours.
          </p>
        </div>
      </div>
    </div>
  );
}
